import React, { memo, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { validators, strings, objects } from 'investira.sdk';

import {
  Stack,
  CircularProgress,
  TextField,
  InputAdornment,
  Icon,
  Typography,
  Dialog,
  Divider,
  IconButton,
  Button,
  Skeleton,
  DialogContent,
  useMediaQuery
} from 'investira.react.components';
import services from '../../../services';
import utils from '../../../utils';
import { PATHS } from '../../../enums';
import withResponseHandling from '../../../hoc/withResponseHandling';
import { acSearchAtivosChanged } from '../../../store/actions';
import SearchAtivosItem from './SearchAtivosItem';

/**
 * Renderiza o componente SearchAtivos.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Function} [props.dataRead] - Consulta usada para carregar as sugestões.
 * @param {Object} [props.dataReadParams] - Filtros aplicados à consulta de sugestões.
 * @param {*} props.pageIsLoading - Propriedade `pageIsLoading` do componente.
 * @param {*} props.clearOnClose - Propriedade `clearOnClose` do componente.
 * @param {string} [props.mercado] - Mercado usado para limitar as sugestões.
 * @param {string} [props.placeholder] - Texto apresentado nos campos de pesquisa.
 * @param {boolean} [props.preserveQuery] - Mantém os filtros atuais ao pesquisar.
 * @param {string} [props.resultsPath] - Rota usada para apresentar todos os resultados.
 * @param {string} [props.searchValue] - Pesquisa textual ativa na URL.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchAtivos = memo(props => {
  const PAGE_SIZE = 10;
  const xPlaceholder = props.placeholder || "Pesquise por ativos (Ações, Fundos, CDBs...)";
  const xMercado = props.mercado || null;
  const xSearchValue = props.searchValue || "";
  const xDataReadParams = props.dataReadParams || {};
  const xBaseRequestContextKey = JSON.stringify({
    mercado: xMercado,
    params: xDataReadParams,
  });

  const search = useSelector(store => store.searchAtivos);

  const location = useLocation();
  const navigate = useNavigate();
  const storeDispatch = useDispatch();

  const [isLoading, setRequesting] = useState(false);
  const [pesquisa, setPesquisa] = useState('');
  const [data, setData] = useState({});
  const [pages, setPages] = useState({});
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(null);
  const [ignoredFiltersContext, setIgnoredFiltersContext] = useState(null);
  const [hasEmptyResult, setHasEmptyResult] = useState(false);
  const xIgnoreFilters = ignoredFiltersContext === xBaseRequestContextKey;
  const xEffectiveDataReadParams = xIgnoreFilters ? {} : xDataReadParams;
  const xRequestContextKey = JSON.stringify({
    context: xBaseRequestContextKey,
    ignoreFilters: xIgnoreFilters,
  });
  const xCanIgnoreAppliedFilters = Boolean(xMercado) && Object.keys(xDataReadParams).length > 0;
  const xCanReuseSearchCache =
    (search.mercado || null) === xMercado &&
    search.contextKey === xRequestContextKey &&
    search.pesquisa === pesquisa &&
    !validators.isEmpty(search.result);

  const inputRef = useRef();
  const placeholderRef = useRef();
  const requestIdRef = useRef(0);
  const requestContextRef = useRef(xRequestContextKey);

  const TOTAL_ITEMS = pages.total_items;

  const handleOpen = pEvent => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    placeholderRef.current.blur();
    inputRef.current.blur();
    setPesquisa(xSearchValue);
    setData({});
    setPages({});
    setMessage(null);
    setHasEmptyResult(false);
    setIgnoredFiltersContext(null);
  };

  const handleChange = pEvent => {
    const xValue = pEvent.currentTarget.value;

    setPesquisa(pEvent.currentTarget.value);
    handleOpen(pEvent);

    if (validators.isEmpty(xValue)) {
      handleClose();
      return;
    }
  };

  const openDrawerOnFocus = pEvent => {
    placeholderRef.current.blur();
    handleOpen(pEvent);
  };

  const handleClearPesquisa = (pLocation = {}) => {
    setPesquisa('');
    setData({});
    setPages({});

    if (!validators.isEmpty(pLocation?.search)) {
      const xSearch = strings.querystringToObject(pLocation.search);

      if (!validators.isEmpty(xSearch.pesquisa)) {
        delete xSearch.pesquisa;
        const xQuery = objects.objectToQueryString(xSearch);
        navigate(`/ativos${xQuery}`);
      }
    }
  };

  const handleClearActiveSearch = pEvent => {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    const xQuery = strings.querystringToObject(location.search) || {};
    delete xQuery.pesquisa;

    const xResultsPath = props.resultsPath || location.pathname;
    setPesquisa('');
    setData({});
    setPages({});
    navigate(`${xResultsPath}${objects.objectToQueryString(xQuery)}`);
  };

  const handleGoTo = (pEvent, pPessoaId, pMercado, pClearOnClose) => {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    const xPath = PATHS[pMercado.toLowerCase()].path;

    handleClose();
    pClearOnClose && handleClearPesquisa();
    navigate(`/ativos${xPath}/${pPessoaId}`);
  };

  const handleSearch = (pPesquisa, pFilters = {}) => {
    const xFilters = objects.deleteNull({ ...pFilters });

    const xPesquisa = validators.isEmpty(pPesquisa) ? search.pesquisa.trim() : pPesquisa.trim();

    const xCurrentQuery = props.preserveQuery
      ? strings.querystringToObject(location.search) || {}
      : {};

    // No modo ampliado, remove somente os filtros opcionais aplicados pelo sidebar.
    if (xIgnoreFilters) {
      Object.keys(xDataReadParams).forEach((pParam) => delete xCurrentQuery[pParam]);
    }

    const xSearch = { ...xCurrentQuery, pesquisa: xPesquisa, ...xFilters };
    const xSearchQueryString = objects.objectToQueryString(xSearch);
    const xResultsPath = props.resultsPath || '/ativos/busca';
    const xUrl = `${xResultsPath}${xSearchQueryString}`;

    handleClose();
    navigate(xUrl);
  };

  /** Repete a consulta mantendo o mercado e ignorando os filtros opcionais. */
  function handleSearchWithoutFilters() {
    setIgnoredFiltersContext(xBaseRequestContextKey);
  }

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, '');
    }

    return pStr;
  }

  // function isNumeric(pStr) {
  //   if (typeof pStr !== "string") {
  //     return false;
  //   }

  //   return /^[0-9]+$/.test(pStr);
  // }

  const request = useMemo(() => {
    const xDataRead = props.dataRead || services.ativosbase.list;

    return utils.helpers.debounce(
      (pParams, pRes, pErr, pFin) => xDataRead(pParams, pRes, pErr, pFin),
      1000,
    );
  }, [props.dataRead]);

  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, ' ').trim();
  }

  function removeEmptyItems(pArr) {
    return pArr.filter(item => item !== '');
  }

  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(' ');
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map(xString => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join('');

    return xSearchFormated;
  }

  useEffect(() => {
    setPesquisa(xSearchValue);
  }, [xSearchValue]);

  // Filtros diferentes representam outro contexto e invalidam resultados pendentes.
  useEffect(() => {
    requestIdRef.current += 1;
    requestContextRef.current = xRequestContextKey;
    setData({});
    setPages({});
    setMessage(null);
    setHasEmptyResult(false);
  }, [xRequestContextKey]);

  useEffect(() => {
    if (open) {
      placeholderRef.current.blur();
      inputRef.current.focus();

      setData(xCanReuseSearchCache ? search.result : {});
      setPages(xCanReuseSearchCache ? search.pages : {});
    }
  }, [open, xRequestContextKey]);

  useEffect(() => {
    if (open && pesquisa && pesquisa.length > 1 && !props.pageIsLoading && !xCanReuseSearchCache) {
      const xRequestId = ++requestIdRef.current;
      const xRequestContext = xRequestContextKey;

      setRequesting(true);
      setData({});
      setMessage(null);
      setHasEmptyResult(false);

      const xPesquisa = formatSearch(pesquisa);

      request(
        {
          ...xEffectiveDataReadParams,
          pesquisa: xPesquisa,
          size: PAGE_SIZE,
          cancelado: 'null',

          sort: 'relevancia DESC, __mr__ DESC, mercado ASC',
          fracionario: 0,
          prazo: 'VST',
          ...(props.mercado && { mercado: props.mercado })
        },
        (rRes) => {
          if (
            requestIdRef.current !== xRequestId ||
            requestContextRef.current !== xRequestContext
          ) {
            return;
          }

          setData(rRes.data);
          setPages(rRes.pages);
          storeDispatch(
            acSearchAtivosChanged({
              mercado: xMercado,
              contextKey: xRequestContext,
              pesquisa: pesquisa,
              result: rRes.data,
              pages: rRes.pages,
            }),
          );

          if (validators.isEmpty(rRes.data)) {
            setHasEmptyResult(true);
            setMessage(
              xCanIgnoreAppliedFilters && !xIgnoreFilters
                ? "Nenhum resultado encontrado com os filtros aplicados."
                : xMercado
                ? "Nenhum resultado encontrado neste mercado."
                : "Nenhum dado encontrado",
            );
          }
        },
        (rErr) => {
          if (
            requestIdRef.current === xRequestId &&
            requestContextRef.current === xRequestContext
          ) {
            setHasEmptyResult(false);
            setMessage("Erro na busca por empresa");
          }
        },
        () => {
          if (
            requestIdRef.current === xRequestId &&
            requestContextRef.current === xRequestContext
          ) {
            setRequesting(false);
          }
        },
      );
    }
  }, [open, pesquisa, props.pageIsLoading, request, xRequestContextKey, xCanReuseSearchCache]);

  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: {
          sx: {
            pt: 3,
            pb: 2,
            px: 2
          }
        },
        maxWidth: 'lg'
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: {
            margin: 0,
            backgroundColor: 'background.dark'
          }
        }
      };

  return (
    <>
      <Stack
        id="search-area"
        sx={{
          position: "relative",
          width: "100%",
          zIndex: "999",
        }}>
        {props.pageIsLoading ? (
          <Skeleton variant="rounded" width="100%" height={56} />
        ) : (
          <TextField
            id="placeholder-busca-empresa"
            inputRef={placeholderRef}
            value={pesquisa}
            onFocus={openDrawerOnFocus}
            variant="outlined"
            placeholder={xPlaceholder}
            size="normal"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="find" size={21} color={'primary'} />
                </InputAdornment>
              ),
              endAdornment: !validators.isEmpty(xSearchValue) && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Limpar pesquisa"
                    color="primary"
                    onClick={handleClearActiveSearch}>
                    <Icon iconName="cancel" size={18} color="primary" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            disabled={props.pageIsLoading}
          />
        )}

        <Dialog {...xDialogProps} open={open} onClose={handleClose} keepMounted fullWidth>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack>
                <IconButton onClick={handleClose} color={'primary'}>
                  <Icon iconName="arrow_left" size={21} />
                </IconButton>
              </Stack>
              <Stack flexGrow={1}>
                <TextField
                  id="busca-empresa"
                  inputRef={inputRef}
                  value={pesquisa}
                  onChange={handleChange}
                  sx={{ border: 'none' }}
                  variant="outlined"
                  size={isUpMd ? 'normal' : 'small'}
                  placeholder={xPlaceholder}
                  fullWidth
                  disabled={props.pageIsLoading}
                />
              </Stack>
            </Stack>
            <Divider />
            <Stack>
              {!isLoading && !validators.isEmpty(data) && (
                <>
                  <Stack id="busca-empresa-list" sx={{ px: 0 }} component="ul">
                    {Object.values(data).map(xAtivo => {
                      return (
                        <SearchAtivosItem
                          key={crypto.randomUUID()}
                          ativo={xAtivo}
                          showAtivoTipo={Boolean(xMercado)}
                          onClick={(e) =>
                            handleGoTo(
                              e,
                              xAtivo.ifeed_ativo_id || xAtivo.ativo_id,
                              xAtivo.mercado,
                              props.clearOnClose,
                            )
                          }
                        />
                      );
                    })}
                  </Stack>
                  <Stack
                    sx={{
                      px: 2,
                      py: 2,
                      alignItems: "center",
                    }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSearch(pesquisa)}
                      sx={{ maxWidth: "500px", px: 4 }}>
                      Visualizar Todos ({TOTAL_ITEMS})
                    </Button>
                  </Stack>
                </>
              )}

              {isLoading && (
                <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}

              {!isLoading && validators.isEmpty(data) && (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                  spacing={2}
                  sx={{ py: 2 }}>
                  <Typography variant="body2" color="textPrimary">
                    {message}
                  </Typography>
                  {hasEmptyResult && xCanIgnoreAppliedFilters && !xIgnoreFilters && (
                    <Button variant="outlined" onClick={handleSearchWithoutFilters}>
                      Pesquisar ignorando os filtros
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      </Stack>
    </>
  );
});

export default withResponseHandling(SearchAtivos);
